import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import { disposeScene } from '../utils/ThreeCleanup';

interface Star {
  name: string;
  position: [number, number, number];
  color: string;
  size: number;
  type: string;
  url?: string; // Add optional URL property
}

const GalaxyVisualization: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const labelContainerRef = useRef<HTMLDivElement>(null);
  const starInfoRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!containerRef.current || !labelContainerRef.current) return;
    
    // Clear any previous labels
    while (labelContainerRef.current.firstChild) {
      labelContainerRef.current.removeChild(labelContainerRef.current.firstChild);
    }
    
    // Setup Three.js scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x031209);
    
    // Camera setup - ZOOM OUT by 50%
    const camera = new THREE.PerspectiveCamera(
      60, 
      containerRef.current.clientWidth / containerRef.current.clientHeight, 
      0.1, 
      1000
    );
    camera.position.z = 45; // Changed from 30 to 45 for 50% zoom out
    
    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    containerRef.current.appendChild(renderer.domElement);
    
    // Create stars data with project-named stars
    const stars: Star[] = [
      { 
        name: "GB4K PRIME", 
        position: [0, 0, 0], 
        color: "#5fe979", 
        size: 2.5, 
        type: "System Center"
      },
      { 
        name: "3causes.org", 
        position: [8, 5, 3], 
        color: "#ff6b6b", 
        size: 1.8, 
        type: "Binary Star",
        url: "https://3causes.org"
      },
      {
        name: "magasify.fr",
        position: [5, -15, -2],
        color: "#118ab2",
        size: 1.3,
        type: "Pulsar",
        url: "https://magasify.fr"
      },
      { 
        name: "Nanou", 
        position: [12, 8, -6], 
        color: "#73d2f3", 
        size: 1.7, 
        type: "Blue Star" 
      },
      { 
        name: "Family Nebula", 
        position: [15, -8, 10], 
        color: "#9b5de5", 
        size: 2.2, 
        type: "Stellar Nursery" 
      },
      { 
        name: "Carrefour", 
        position: [-8, -9, -5], 
        color: "#f8f9fa", 
        size: 1.4, 
        type: "White Dwarf" 
      },
      { 
        name: "Rotary", 
        position: [-10, 12, 7], 
        color: "#e63946", 
        size: 2.0, 
        type: "Red Giant" 
      },
      { 
        name: "Ararat world record", 
        position: [18, 6, 15], 
        color: "#457b9d", 
        size: 1.0, 
        type: "Distant Star" 
      },
      { 
        name: "Secret", 
        position: [13, -5, -10], 
        color: "#000000", 
        size: 1.5, 
        type: "Black Hole" 
      },
      {
        name: "gb4k.fr",
        position: [-5, -7, 12],
        color: "#2ec4b6",
        size: 1.6,
        type: "Home Planet",
        url: "https://gb4k.fr"
      },
      // Karakoto (page active du site)
      {
        name: "Karakoto",
        position: [-15, 10, -5],
        color: "#e6b800",
        size: 1.6,
        type: "Golden Sanctuary"
      },
      // Books — one star per published book
      {
        name: "Level Up",
        position: [-9, 15, 8],
        color: "#00e5ff",
        size: 1.5,
        type: "Book · Personal Growth"
      },
      {
        name: "Rotary Disaster",
        position: [-16, 4, 11],
        color: "#ff8c42",
        size: 1.5,
        type: "Book · Field Guide"
      },
      {
        name: "Évangile Éternel",
        position: [3, 17, -7],
        color: "#c8a2ff",
        size: 1.5,
        type: "Book · Theology"
      },
      {
        name: "Harcèlement Moral",
        position: [-7, -17, -6],
        color: "#5b8def",
        size: 1.5,
        type: "Book · Survival Guide"
      },
      {
        name: "Tristan",
        position: [-14, -16, 9], 
        color: "#4361ee", 
        size: 1.9, 
        type: "Guardian Star" 
      },
      { 
        name: "Clémence", 
        position: [20, -4, 8], 
        color: "#fb8500", 
        size: 1.9, 
        type: "Luminous Giant" 
      },
      // Add Memory and Remember as black stars
      { 
        name: "Memory", 
        position: [-20, 10, 12], 
        color: "#000000", 
        size: 1.5, 
        type: "Dark Void" 
      },
      { 
        name: "Remember", 
        position: [17, -17, 6], 
        color: "#000000", 
        size: 1.5, 
        type: "Forgotten Archive" 
      }
    ];

    // Add stars to scene
    const starGeometries: THREE.Mesh[] = [];
    const starObjects: {mesh: THREE.Mesh, star: Star}[] = [];
    
    stars.forEach(star => {
      // Create star
      const geometry = new THREE.SphereGeometry(star.size, 32, 32);
      const material = new THREE.MeshBasicMaterial({ color: star.color });
      
      // For black hole, make it look special
      if (star.name === "Secret" || star.name === "Memory" || star.name === "Remember") {
        material.transparent = true;
        material.opacity = 0.8;
      }
      
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(...star.position);
      scene.add(mesh);
      starGeometries.push(mesh);
      starObjects.push({mesh, star});
      
      // Add glow effect for some stars
      if (star.name === "GB4K PRIME" || star.type.includes("Giant") || star.name === "Nanou" || star.name === "Tristan" || star.name === "Clémence") {
        const glowMaterial = new THREE.ShaderMaterial({
          uniforms: {
            "c": { value: 0.2 },
            "p": { value: 1.2 },
            glowColor: { value: new THREE.Color(star.color) },
            viewVector: { value: new THREE.Vector3() }
          },
          vertexShader: `
            uniform vec3 viewVector;
            uniform float c;
            uniform float p;
            varying float intensity;
            void main() {
              vec3 vNormal = normalize(normal);
              vec3 vNormel = normalize(viewVector);
              intensity = pow(c - dot(vNormal, vNormel), p);
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
          `,
          fragmentShader: `
            uniform vec3 glowColor;
            varying float intensity;
            void main() {
              vec3 glow = glowColor * intensity;
              gl_FragColor = vec4(glow, 1.0);
            }
          `,
          side: THREE.BackSide,
          blending: THREE.AdditiveBlending,
          transparent: true
        });
        
        const glowGeometry = new THREE.SphereGeometry(star.size * 1.5, 32, 32);
        const glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
        glowMesh.position.set(...star.position);
        scene.add(glowMesh);
      }
      
      // Create label for each star
      const labelDiv = document.createElement('div');
      labelDiv.className = 'absolute pointer-events-none opacity-0 transition-opacity';
      labelDiv.style.fontFamily = 'C64, monospace';
      labelDiv.style.color = star.color;
      labelDiv.style.fontSize = '12px';
      labelDiv.style.fontWeight = 'bold';
      labelDiv.style.textShadow = `0 0 5px ${star.color}`;
      labelDiv.style.whiteSpace = 'nowrap';
      labelDiv.textContent = star.name;
      labelContainerRef.current?.appendChild(labelDiv);
    });
    
    // Add connecting lines between stars (like constellations)
    const connections = [
      { from: "GB4K PRIME", to: "3causes.org" },
      { from: "GB4K PRIME", to: "Nanou" },
      { from: "GB4K PRIME", to: "gb4k.fr" },
      { from: "GB4K PRIME", to: "magasify.fr" },
      { from: "3causes.org", to: "Rotary" },
      { from: "magasify.fr", to: "Carrefour" },
      { from: "Nanou", to: "Family Nebula" },
      { from: "Tristan", to: "GB4K PRIME" },
      { from: "Clémence", to: "GB4K PRIME" },
      { from: "Tristan", to: "Clémence" },
      // Constellation des livres — rayonnant depuis GB4K PRIME
      { from: "GB4K PRIME", to: "Level Up" },
      { from: "GB4K PRIME", to: "Rotary Disaster" },
      { from: "GB4K PRIME", to: "Évangile Éternel" },
      { from: "GB4K PRIME", to: "Harcèlement Moral" },
      { from: "Rotary Disaster", to: "Rotary" },
      { from: "Karakoto", to: "GB4K PRIME" },
    ];
    
    connections.forEach(connection => {
      const star1 = stars.find(s => s.name === connection.from);
      const star2 = stars.find(s => s.name === connection.to);
      
      if (star1 && star2) {
        const points = [];
        points.push(new THREE.Vector3(...star1.position));
        points.push(new THREE.Vector3(...star2.position));
        
        const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
        const lineMaterial = new THREE.LineBasicMaterial({ 
          color: 0x245032,
          opacity: 0.6,
          transparent: true
        });
        
        const line = new THREE.Line(lineGeometry, lineMaterial);
        scene.add(line);
      }
    });

    // Create particle dust effect for galaxy
    const particleGeometry = new THREE.BufferGeometry();
    const particleCount = 2000;
    
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    
    const color = new THREE.Color();
    
    for (let i = 0; i < particleCount * 3; i += 3) {
      // Position in disc shape with some height variation
      const radius = THREE.MathUtils.randFloat(5, 40);
      const theta = THREE.MathUtils.randFloat(0, Math.PI * 2);
      
      positions[i] = radius * Math.cos(theta);
      positions[i + 1] = THREE.MathUtils.randFloatSpread(10);
      positions[i + 2] = radius * Math.sin(theta);
      
      // Colors - subtle greens and blues
      const hue = THREE.MathUtils.randFloat(0.3, 0.5); // Green to blue
      const saturation = THREE.MathUtils.randFloat(0.4, 0.8);
      const lightness = THREE.MathUtils.randFloat(0.3, 0.6);
      
      color.setHSL(hue, saturation, lightness);
      
      colors[i] = color.r;
      colors[i + 1] = color.g;
      colors[i + 2] = color.b;
    }
    
    particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    particleGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    
    const particleMaterial = new THREE.PointsMaterial({
      size: 0.1,
      vertexColors: true,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending
    });
    
    const particleSystem = new THREE.Points(particleGeometry, particleMaterial);
    scene.add(particleSystem);

    // Setup raycaster for star interaction
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    let intersectedObject: THREE.Mesh | null = null;
    
    // Function to update star info panel
    const updateStarInfo = (star: Star | null) => {
      if (!starInfoRef.current) return;
      
      if (star) {
        starInfoRef.current.style.opacity = '1';
        
        // Indicate if star is clickable (has URL)
        const hasUrl = star.url ? `<div class="text-sm text-cyan-300">(Click to visit website)</div>` : '';
        
        starInfoRef.current.innerHTML = `
          <div style="color: ${star.color};">
            <div class="text-xl font-bold">${star.name}</div>
            <div class="text-sm">${star.type}</div>
            ${hasUrl}
          </div>
        `;
      } else {
        starInfoRef.current.style.opacity = '0';
      }
    };

    // Add event listeners
    const onMouseMove = (event: MouseEvent) => {
      if (!containerRef.current) return;
      
      // Calculate mouse position
      const rect = containerRef.current.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / containerRef.current.clientWidth) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / containerRef.current.clientHeight) * 2 + 1;
      
      // Update the picking ray with the camera and mouse position
      raycaster.setFromCamera(mouse, camera);
      
      // Calculate objects intersecting the picking ray
      const intersects = raycaster.intersectObjects(starGeometries);
      
      // Reset previously intersected object
      if (intersectedObject) {
        const starData = starObjects.find(so => so.mesh === intersectedObject);
        if (starData && starData.star.name !== "Secret" && starData.star.name !== "Memory" && starData.star.name !== "Remember") {
          intersectedObject.scale.set(1, 1, 1);
        }
      }
      
      // Handle container labels
      if (!labelContainerRef.current) return;
      
      // Hide all labels first
      Array.from(labelContainerRef.current.children).forEach(child => {
        (child as HTMLElement).style.opacity = '0';
      });
      
      // Update currently intersected object
      if (intersects.length > 0) {
        intersectedObject = intersects[0].object as THREE.Mesh;
        const starData = starObjects.find(so => so.mesh === intersectedObject);
        
        if (starData) {
          // Highlight star
          if (starData.star.name !== "Secret" && starData.star.name !== "Memory" && starData.star.name !== "Remember") {
            intersectedObject.scale.set(1.2, 1.2, 1.2);
          }
          
          // Update info panel
          updateStarInfo(starData.star);
          
          // Show label for this star
          const starIndex = stars.findIndex(s => s.name === starData.star.name);
          if (starIndex >= 0 && starIndex < labelContainerRef.current.children.length) {
            const labelElement = labelContainerRef.current.children[starIndex] as HTMLElement;
            
            // Project position to screen space
            const position = new THREE.Vector3(...starData.star.position);
            position.project(camera);
            
            // Convert to CSS coordinates
            const x = (position.x * 0.5 + 0.5) * containerRef.current.clientWidth;
            const y = -(position.y * 0.5 - 0.5) * containerRef.current.clientHeight;
            
            labelElement.style.left = `${x}px`;
            labelElement.style.top = `${y - 25}px`;
            labelElement.style.opacity = '1';
            
            // Change cursor if star has a URL
            if (starData.star.url) {
              containerRef.current.style.cursor = 'pointer';
            } else {
              containerRef.current.style.cursor = 'default';
            }
          }
        }
      } else {
        intersectedObject = null;
        updateStarInfo(null);
        containerRef.current.style.cursor = 'default';
      }
    };
    
    // Handle click on stars
    const onMouseClick = (_event: MouseEvent) => {
      if (!containerRef.current) return;
      
      // Update the picking ray with the camera and mouse position
      raycaster.setFromCamera(mouse, camera);
      
      // Calculate objects intersecting the picking ray
      const intersects = raycaster.intersectObjects(starGeometries);
      
      if (intersects.length > 0) {
        const clickedObject = intersects[0].object as THREE.Mesh;
        const starData = starObjects.find(so => so.mesh === clickedObject);
        
        if (starData && starData.star.url) {
          // Open the URL in a new tab
          window.open(starData.star.url, '_blank');
        }
      }
    };
    
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('click', onMouseClick);

    // Touch support for mobile
    const onTouchStart = (event: TouchEvent) => {
      if (!containerRef.current || event.touches.length === 0) return;
      const touch = event.touches[0];
      const rect = containerRef.current.getBoundingClientRect();
      mouse.x = ((touch.clientX - rect.left) / containerRef.current.clientWidth) * 2 - 1;
      mouse.y = -((touch.clientY - rect.top) / containerRef.current.clientHeight) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(starGeometries);
      if (intersects.length > 0) {
        const starData = starObjects.find(so => so.mesh === intersects[0].object);
        if (starData?.star.url) {
          window.open(starData.star.url, '_blank');
        }
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });

    // Animation loop
    let rotationSpeed = 0.001;
    let autoRotate = true;
    
    const clock = new THREE.Clock();
    
    const animate = () => {
      const delta = clock.getDelta();
      
      if (autoRotate) {
        scene.rotation.y += rotationSpeed * delta * 60;
        particleSystem.rotation.y -= rotationSpeed * 0.5 * delta * 60;
      }
      
      // Make stars subtly pulse
      starObjects.forEach(({ mesh, star }) => {
        if (star.name !== "Secret" && star.name !== "Memory" && star.name !== "Remember" && mesh !== intersectedObject) {
          const time = Date.now() * 0.001;
          const pulse = Math.sin(time + star.position[0]) * 0.05 + 1;
          mesh.scale.set(pulse, pulse, pulse);
        }
      });
      
      renderer.render(scene, camera);
      animationFrameId = requestAnimationFrame(animate);
    };

    let animationFrameId = requestAnimationFrame(animate);
    
    // Handle container resizing
    const handleResize = () => {
      if (!containerRef.current) return;
      
      camera.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Add event listener for toggling auto-rotation
    const handleClick = () => {
      autoRotate = !autoRotate;
    };
    
    containerRef.current.addEventListener('click', handleClick);
    
    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameId);
      if (containerRef.current) {
        containerRef.current.removeEventListener('click', handleClick);
        if (containerRef.current.contains(renderer.domElement)) {
          containerRef.current.removeChild(renderer.domElement);
        }
      }

      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('click', onMouseClick);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('resize', handleResize);
      disposeScene(scene, renderer);
    };
  }, []);
  
  return (
    <div className="w-full h-full flex flex-col">
      <div className="relative flex-grow" ref={containerRef}>
        <div ref={labelContainerRef} className="absolute inset-0 pointer-events-none"></div>
        
        <div 
          ref={starInfoRef} 
          className="absolute bottom-4 right-4 bg-[#131616] bg-opacity-80 p-3 rounded border border-[var(--terminal-border)] transition-opacity duration-200 opacity-0"
        ></div>
        
        <div className="absolute top-4 left-4 bg-[#131616] bg-opacity-80 p-3 rounded border border-[var(--terminal-border)]">
          <p className="text-sm text-[var(--terminal-text)]">
            <span className="font-bold">GB4K STELLAR CARTOGRAPHY</span><br />
            Click to toggle rotation<br />
            Hover over stars for details<br />
            Click on website stars to visit
          </p>
        </div>
      </div>
    </div>
  );
};

export default GalaxyVisualization;